import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, TextField, Button, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Chip, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControlLabel, Switch, Divider
} from '@mui/material';
import { Save, Add, Delete, Star, Edit } from '@mui/icons-material';
import settingsService from '../../services/settings.service';
import groupsService from '../../services/groups.service';
import NumberSequencesTab from './NumberSequencesTab';
import FinancialDimensionsTab from './FinancialDimensionsTab';
import CompaniesTab from './CompaniesTab';
import { useLanguage } from '../../i18n/LanguageContext';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ py: 3 }}>{children}</Box>}</div>
);

// ==================== COMPANY INFO TAB ====================
const CompanyInfoTab: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [form, setForm] = useState({
    company_name: '', company_address: '', company_city: '', company_country: '',
    company_phone: '', company_email: '', company_website: '',
    company_tax_number: '', company_registration_number: '',
    company_bank_name: '', company_bank_account: '', company_bank_swift: '',
    invoice_prefix: 'INV', invoice_footer_text: 'Thank you for your business!',
    invoice_payment_terms: 'Net 30', default_language: language
  });

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await settingsService.getCompanySettingsFlat();
      const map: any = {};
      res.data.forEach((s: any) => { map[s.key] = s.value; });
      setForm(prev => ({ ...prev, ...map }));
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    setResult(null);
    try {
      const settings = Object.entries(form).map(([key, value]) => ({
        key,
        value: value || '',
        category: key.startsWith('invoice_') ? 'INVOICE' : 'GENERAL'
      }));
      await settingsService.saveCompanySettings(settings);
      setResult({ type: 'success', msg: t('settings.saved_ok') });
      window.dispatchEvent(new Event('companySettingsUpdated'));

      // Apply language change immediately
      if (form.default_language !== language) {
        setLanguage(form.default_language as 'en' | 'cs');
      }
    } catch (err: any) {
      setResult({ type: 'error', msg: err.response?.data?.message || 'Error saving settings' });
    }
    finally { setSaving(false); }
  };

  const field = (labelKey: string, key: string, props: any = {}) => (
    <TextField fullWidth label={t(labelKey)} value={(form as any)[key] || ''}
      onChange={e => setForm({ ...form, [key]: e.target.value })} {...props} />
  );

  return (
    <Box>
      {result && <Alert severity={result.type} sx={{ mb: 2 }}>{result.msg}</Alert>}

      <Typography variant="h6" sx={{ mb: 2 }}>{t('settings.company_details')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {field('settings.company_name', 'company_name')}
        {field('settings.company_tax_number', 'company_tax_number')}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {field('settings.company_registration', 'company_registration_number')}
        {field('settings.company_website', 'company_website')}
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('settings.address_section')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {field('settings.company_address', 'company_address')}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {field('settings.company_city', 'company_city')}
        {field('settings.company_country', 'company_country')}
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('settings.contact_section')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {field('settings.company_phone', 'company_phone')}
        {field('settings.company_email', 'company_email', { type: 'email' })}
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('settings.banking_section')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {field('settings.bank_name', 'company_bank_name')}
        {field('settings.bank_account', 'company_bank_account')}
        {field('settings.bank_swift', 'company_bank_swift')}
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('settings.invoice_defaults')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {field('settings.invoice_prefix', 'invoice_prefix', { sx: { maxWidth: 150 } })}
        {field('settings.payment_terms_default', 'invoice_payment_terms')}
      </Box>
      <Box sx={{ mb: 2 }}>
        {field('settings.invoice_footer', 'invoice_footer_text', { multiline: true, rows: 2 })}
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('settings.default_language')}</Typography>
      <Box sx={{ mb: 3 }}>
        <TextField select label={t('settings.default_language')} value={form.default_language || 'en'}
          onChange={e => setForm({ ...form, default_language: e.target.value as 'en' | 'cs' })} sx={{ minWidth: 250 }}>
          <MenuItem value="en">{t('settings.language_english')}</MenuItem>
          <MenuItem value="cs">{t('settings.language_czech')}</MenuItem>
        </TextField>
      </Box>

      <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving} size="large">
        {saving ? t('common.saving') : t('settings.save_settings')}
      </Button>
    </Box>
  );
};

// ==================== CURRENCIES TAB ====================
const CurrenciesTab: React.FC = () => {
  const { t } = useLanguage();
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [dialog, setDialog] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ code: '', name: '', symbol: '', decimalPlaces: 2, isDefault: false, isActive: true });

  useEffect(() => { loadCurrencies(); }, []);

  const loadCurrencies = async () => {
    try { const res = await settingsService.getCurrencies(true); setCurrencies(res.data); }
    catch (err) { console.error(err); }
  };

  const handleOpen = (currency?: any) => {
    if (currency) {
      setEditId(currency.id);
      setForm({
        code: currency.code, name: currency.name, symbol: currency.symbol,
        decimalPlaces: currency.decimalPlaces, isDefault: currency.isDefault, isActive: currency.isActive
      });
    } else {
      setEditId(null);
      setForm({ code: '', name: '', symbol: '', decimalPlaces: 2, isDefault: false, isActive: true });
    }
    setDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editId) { await settingsService.updateCurrency(editId, form); }
      else { await settingsService.createCurrency(form); }
      setDialog(false);
      loadCurrencies();
    } catch (err: any) { alert(err.response?.data?.message || 'Error saving currency'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('settings.delete_currency_confirm'))) return;
    try { await settingsService.deleteCurrency(id); loadCurrencies(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error deleting currency'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('settings.currencies')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>{t('settings.add_currency')}</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('common.code')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('common.name')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Symbol</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('settings.currency_decimals')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('settings.currency_default')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">{t('common.active')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currencies.map(c => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpen(c)}>
                <TableCell><strong>{c.code}</strong></TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.symbol}</TableCell>
                <TableCell align="center">{c.decimalPlaces}</TableCell>
                <TableCell align="center">{c.isDefault && <Star color="warning" fontSize="small" />}</TableCell>
                <TableCell align="center">
                  <Chip label={c.isActive ? t('common.active') : t('common.inactive')}
                    color={c.isActive ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(c.id); }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {currencies.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('settings.no_currencies')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? t('settings.edit_currency') : t('settings.add_currency')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField fullWidth required label={t('settings.currency_code')} value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              inputProps={{ maxLength: 3 }} />
            <TextField fullWidth required label={t('settings.currency_symbol')} value={form.symbol}
              onChange={e => setForm({ ...form, symbol: e.target.value })} inputProps={{ maxLength: 5 }} />
          </Box>
          <TextField fullWidth required label={t('settings.currency_name')} value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label={t('settings.currency_decimals')} type="number" value={form.decimalPlaces}
            onChange={e => setForm({ ...form, decimalPlaces: Number(e.target.value) })}
            sx={{ mt: 2 }} inputProps={{ min: 0, max: 4 }} />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel control={
              <Switch checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />
            } label={t('settings.currency_default')} />
            <FormControlLabel control={
              <Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
            } label={t('common.active')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.code || !form.name || !form.symbol}>
            {editId ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ==================== EXCHANGE RATES TAB ====================
const ExchangeRatesTab: React.FC = () => {
  const { t } = useLanguage();
  const [rates, setRates] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ fromCurrencyId: '', toCurrencyId: '', rate: '', effectiveDate: new Date().toISOString().split('T')[0] });

  useEffect(() => { loadRates(); loadCurrencies(); }, []);

  const loadRates = async () => {
    try { const res = await settingsService.getExchangeRates(); setRates(res.data); }
    catch (err) { console.error(err); }
  };

  const loadCurrencies = async () => {
    try { const res = await settingsService.getCurrencies(); setCurrencies(res.data); }
    catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    try {
      await settingsService.createExchangeRate({
        fromCurrencyId: Number(form.fromCurrencyId),
        toCurrencyId: Number(form.toCurrencyId),
        rate: Number(form.rate),
        effectiveDate: form.effectiveDate
      });
      setDialog(false);
      loadRates();
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('settings.delete_rate_confirm'))) return;
    try { await settingsService.deleteExchangeRate(id); loadRates(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('settings.exchange_rates')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => {
          setForm({ fromCurrencyId: '', toCurrencyId: '', rate: '', effectiveDate: new Date().toISOString().split('T')[0] });
          setDialog(true);
        }}>{t('settings.add_rate')}</Button>
      </Box>
      {currencies.length < 2 && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('settings.min_currencies')}</Alert>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('settings.from_currency')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('settings.to_currency')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('settings.rate')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('settings.effective_date')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rates.map(r => (
              <TableRow key={r.id}>
                <TableCell><strong>{r.fromCurrencyCode}</strong></TableCell>
                <TableCell><strong>{r.toCurrencyCode}</strong></TableCell>
                <TableCell align="right">{Number(r.rate).toFixed(4)}</TableCell>
                <TableCell>{new Date(r.effectiveDate).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {rates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('settings.no_rates')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('settings.add_rate')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField fullWidth select label={t('settings.from_currency')} value={form.fromCurrencyId}
              onChange={e => setForm({ ...form, fromCurrencyId: e.target.value })}>
              {currencies.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
            </TextField>
            <TextField fullWidth select label={t('settings.to_currency')} value={form.toCurrencyId}
              onChange={e => setForm({ ...form, toCurrencyId: e.target.value })}>
              {currencies.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth required label={t('settings.rate')} type="number" value={form.rate}
              onChange={e => setForm({ ...form, rate: e.target.value })} inputProps={{ step: '0.0001', min: 0 }} />
            <TextField fullWidth required type="date" label={t('settings.effective_date')} value={form.effectiveDate}
              onChange={e => setForm({ ...form, effectiveDate: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={!form.fromCurrencyId || !form.toCurrencyId || !form.rate}>{t('common.create')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ==================== GROUPS TAB ====================
const GroupsTab: React.FC = () => {
  const { t } = useLanguage();
  const [supplierGroups, setSupplierGroups] = useState<any[]>([]);
  const [customerGroups, setCustomerGroups] = useState<any[]>([]);
  const [dialog, setDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'supplier' | 'customer'>('supplier');
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ code: '', description: '' });

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    try { const res = await groupsService.getSupplierGroups(); setSupplierGroups(res.data); } catch { }
    try { const res = await groupsService.getCustomerGroups(); setCustomerGroups(res.data); } catch { }
  };

  const handleOpen = (type: 'supplier' | 'customer', group?: any) => {
    setDialogType(type);
    if (group) {
      setEditId(group.id);
      setForm({ code: group.code, description: group.description });
    } else {
      setEditId(null);
      setForm({ code: '', description: '' });
    }
    setDialog(true);
  };

  const handleSave = async () => {
    try {
      if (dialogType === 'supplier') {
        if (editId) await groupsService.updateSupplierGroup(editId, form);
        else await groupsService.createSupplierGroup(form);
      } else {
        if (editId) await groupsService.updateCustomerGroup(editId, form);
        else await groupsService.createCustomerGroup(form);
      }
      setDialog(false);
      loadGroups();
    } catch (err: any) { alert(err.response?.data?.message || 'Error saving group'); }
  };

  const handleDelete = async (type: 'supplier' | 'customer', id: number) => {
    if (!window.confirm(t('groups.delete_confirm'))) return;
    try {
      if (type === 'supplier') await groupsService.deleteSupplierGroup(id);
      else await groupsService.deleteCustomerGroup(id);
      loadGroups();
    } catch (err: any) { alert(err.response?.data?.message || 'Error deleting group'); }
  };

  const renderGroupTable = (type: 'supplier' | 'customer', groups: any[]) => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">{type === 'supplier' ? t('groups.supplier_groups') : t('groups.customer_groups')}</Typography>
        <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => handleOpen(type)}>
          {t('groups.new_group')}
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('groups.group_code')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('groups.group_name')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} width={100}>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(g => (
              <TableRow key={g.id} hover>
                <TableCell><strong>{g.code}</strong></TableCell>
                <TableCell>{g.description}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => handleOpen(type, g)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(type, g.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {groups.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {t('groups.no_groups')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box>
      {renderGroupTable('supplier', supplierGroups)}
      {renderGroupTable('customer', customerGroups)}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? t('groups.edit_group') : t('groups.new_group')}</DialogTitle>
        <DialogContent>
          <TextField fullWidth required label={t('groups.group_code')} value={form.code}
            onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} sx={{ mt: 1 }}
            inputProps={{ maxLength: 20 }} />
          <TextField fullWidth required label={t('groups.group_name')} value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.code || !form.description}>
            {editId ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ==================== MAIN SETTINGS PAGE ====================
const Settings: React.FC = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>{t('settings.title')}</Typography>
      <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={t('settings.company_info')} />
          <Tab label={t('settings.companies')} />
          <Tab label={t('settings.currencies')} />
          <Tab label={t('settings.exchange_rates')} />
          <Tab label={t('settings.groups')} />
          <Tab label={t('settings.number_sequences')} />
          <Tab label={t('settings.dimensions')} />
        </Tabs>
      </Paper>
      <TabPanel value={tab} index={0}><CompanyInfoTab /></TabPanel>
      <TabPanel value={tab} index={1}><CompaniesTab /></TabPanel>
      <TabPanel value={tab} index={2}><CurrenciesTab /></TabPanel>
      <TabPanel value={tab} index={3}><ExchangeRatesTab /></TabPanel>
      <TabPanel value={tab} index={4}><GroupsTab /></TabPanel>
      <TabPanel value={tab} index={5}><NumberSequencesTab /></TabPanel>
      <TabPanel value={tab} index={6}><FinancialDimensionsTab /></TabPanel>
    </Box>
  );
};

export default Settings;
